import { EditableText, EditableTypography, RichTextValue } from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { NoPropagateBox } from "../../../commons/NoPropagateBox.js";
import { PropertiesGridLabeledValue } from "../../../commons/PropertiesGridLabeledValue.js";
import {
  itemHasProperties,
  useQuoteItemProperties,
} from "../../item-properties/itemProperties.js";
import { QuoteProductsVisibilityFragment } from "../Quotes.generated.js";
import { QuotePreviewItemDescription_ItemFragment } from "./QuotePreviewItemDescription.generated.js";
import { PendingChangeValue } from "./QuotePreviewValues.js";

export type QuotePreviewItemDescriptionEditing = {
  onChangeDescription: (value: string) => Promise<void> | void;
  onChangeProductTitle: (value: string) => Promise<void> | void;
  onChangeProductDescription: (value: string) => Promise<void> | void;
};

type Props = {
  item: QuotePreviewItemDescription_ItemFragment;
  productsVisibility: QuoteProductsVisibilityFragment;
  columns?: number;
  // When set, the description / product title / product description become inline-editable.
  // If a pendingChangeAttributes value is present for a field, the editor binds to the
  // PENDING value (so the user refines the pending edit) and the current value renders
  // strikethrough alongside.
  editing?: QuotePreviewItemDescriptionEditing;
};

export const QuotePreviewItemDescription = ({
  item,
  productsVisibility,
  columns = 2,
  editing,
}: Props) => {
  const { t } = useTranslate(["Quote"]);
  const [properties, productProperties] = useQuoteItemProperties(item);

  const pendingDescription = item.pendingChangeAttributes?.description;

  const descriptionBody: React.ReactNode = (() => {
    if (pendingDescription !== undefined) {
      // Level 1: pending change present — value = OLD (agreed, strikethrough),
      //                                   changedValue = NEW (pending, canonical)
      const oldValue = item.description ? (
        <RichTextValue key={item.description} htmlContent={item.description} />
      ) : undefined;
      if (editing) {
        // Level 2: changedValue slot becomes the editor bound to the pending value
        return (
          <PendingChangeValue
            value={oldValue}
            changedValue={
              <NoPropagateBox preventDefault={false}>
                <EditableText
                  value={pendingDescription}
                  placeholder={t("Description", { ns: "Quote" })}
                  onChange={value => {
                    void editing.onChangeDescription(value);
                  }}
                />
              </NoPropagateBox>
            }
          />
        );
      }
      // Level 2: read-only — changedValue slot renders the pending value plain
      return (
        <PendingChangeValue
          value={oldValue}
          changedValue={
            <RichTextValue
              key={pendingDescription}
              htmlContent={pendingDescription}
            />
          }
        />
      );
    }
    // Level 1: no pending change
    if (editing && item.description) {
      // Level 2: editing the current value
      return (
        <NoPropagateBox preventDefault={false}>
          <EditableText
            value={item.description}
            placeholder={t("Description", { ns: "Quote" })}
            onChange={value => {
              void editing.onChangeDescription(value);
            }}
          />
        </NoPropagateBox>
      );
    }
    // Level 2: read-only current value
    if (item.description) {
      return (
        <RichTextValue key={item.description} htmlContent={item.description} />
      );
    }
    return null;
  })();

  const ItemDescription =
    descriptionBody || properties.length > 0 ? (
      <Stack direction="column" spacing={1}>
        {descriptionBody ? (
          <Typography
            variant="body2"
            component="div"
            sx={{ wordBreak: "break-word" }}
          >
            {descriptionBody}
          </Typography>
        ) : null}
        {properties.length > 0 ? (
          <PropertiesGridLabeledValue
            properties={properties}
            columns={columns}
          />
        ) : null}
      </Stack>
    ) : null;

  const productTitleCurrent =
    item.product &&
    item.product.title &&
    item.product.titleClientVisibility !== "hide"
      ? item.product.title
      : "";
  // Pending presence is `!== undefined` (a pending change that clears the
  // field to "" must still count), matching the pending-change semantics used
  // by `PreviewValue` and the data-grid columns. Hidden-visibility pending
  // values collapse to `undefined` (absent), not "".
  const pendingProductTitle =
    item.pendingChangeAttributes?.productTitle !== undefined &&
    item.pendingChangeAttributes?.productTitleClientVisibility !== "hide"
      ? item.pendingChangeAttributes.productTitle
      : undefined;

  const productTitleVisible =
    (productTitleCurrent && productsVisibility.showTitle) ||
    (pendingProductTitle !== undefined && productsVisibility.showTitle);

  const ProductTitle: React.ReactNode = (() => {
    if (!productTitleVisible || !item.product) return null;
    if (pendingProductTitle !== undefined) {
      // Level 1: pending change present — value = OLD (agreed), changedValue = NEW (pending)
      const oldValue = productTitleCurrent ? (
        <Typography
          variant="h4"
          sx={{ fontWeight: "normal", wordBreak: "break-word" }}
        >
          {productTitleCurrent}
        </Typography>
      ) : undefined;
      if (editing) {
        // Level 2: changedValue slot becomes the editor bound to the pending value
        return (
          <PendingChangeValue
            value={oldValue}
            changedValue={
              <NoPropagateBox preventDefault={false}>
                <EditableTypography
                  variant="h4"
                  sx={{ fontWeight: "normal", wordBreak: "break-word" }}
                  value={pendingProductTitle}
                  allowEmpty={false}
                  placeholder={t("Product title", { ns: "Quote" })}
                  onChange={value => {
                    void editing.onChangeProductTitle(value);
                  }}
                />
              </NoPropagateBox>
            }
          />
        );
      }
      // Level 2: read-only — changedValue slot renders the pending value plain
      return (
        <PendingChangeValue
          value={oldValue}
          changedValue={
            <Typography
              variant="h4"
              sx={{ fontWeight: "normal", wordBreak: "break-word" }}
            >
              {pendingProductTitle}
            </Typography>
          }
        />
      );
    }
    // Level 1: no pending change
    if (editing) {
      // Level 2: editing the current value
      return (
        <NoPropagateBox preventDefault={false}>
          <EditableTypography
            variant="h4"
            sx={{ fontWeight: "normal", wordBreak: "break-word" }}
            value={productTitleCurrent}
            allowEmpty={false}
            placeholder={t("Product title", { ns: "Quote" })}
            onChange={value => {
              void editing.onChangeProductTitle(value);
            }}
          />
        </NoPropagateBox>
      );
    }
    // Level 2: read-only current value
    return (
      <Typography
        variant="h4"
        sx={{ fontWeight: "normal", wordBreak: "break-word" }}
      >
        {productTitleCurrent}
      </Typography>
    );
  })();

  const productDescriptionCurrent =
    item.product &&
    item.product.description &&
    item.product?.descriptionClientVisibility !== "hide"
      ? item.product.description
      : "";
  // See `pendingProductTitle` above — pending presence is `!== undefined` so a
  // pending clear ("") still surfaces the overlay; hidden-visibility pending
  // values collapse to `undefined`.
  const pendingProductDescription =
    item.pendingChangeAttributes?.productDescription !== undefined &&
    item.pendingChangeAttributes?.productDescriptionClientVisibility !== "hide"
      ? item.pendingChangeAttributes.productDescription
      : undefined;

  const productDescriptionVisible =
    (productDescriptionCurrent && productsVisibility.showDescription) ||
    (pendingProductDescription !== undefined &&
      productsVisibility.showDescription);

  const productDescriptionBody: React.ReactNode = (() => {
    if (!productDescriptionVisible || !item.product) return null;
    if (pendingProductDescription !== undefined) {
      // Level 1: pending change present — value = OLD (agreed), changedValue = NEW (pending)
      const oldValue = productDescriptionCurrent ? (
        <RichTextValue
          key={productDescriptionCurrent}
          htmlContent={productDescriptionCurrent}
        />
      ) : undefined;
      if (editing) {
        // Level 2: changedValue slot becomes the editor bound to the pending value
        return (
          <PendingChangeValue
            value={oldValue}
            changedValue={
              <NoPropagateBox preventDefault={false}>
                <EditableText
                  value={pendingProductDescription}
                  placeholder={t("Product description", { ns: "Quote" })}
                  onChange={value => {
                    void editing.onChangeProductDescription(value);
                  }}
                />
              </NoPropagateBox>
            }
          />
        );
      }
      // Level 2: read-only — changedValue slot renders the pending value plain
      return (
        <PendingChangeValue
          value={oldValue}
          changedValue={
            <RichTextValue
              key={pendingProductDescription}
              htmlContent={pendingProductDescription}
            />
          }
        />
      );
    }
    // Level 1: no pending change
    if (editing && productDescriptionCurrent) {
      // Level 2: editing the current value
      return (
        <NoPropagateBox preventDefault={false}>
          <EditableText
            value={productDescriptionCurrent}
            placeholder={t("Product description", { ns: "Quote" })}
            onChange={value => {
              void editing.onChangeProductDescription(value);
            }}
          />
        </NoPropagateBox>
      );
    }
    // Level 2: read-only current value
    if (productDescriptionCurrent) {
      return (
        <RichTextValue
          key={productDescriptionCurrent}
          htmlContent={productDescriptionCurrent}
        />
      );
    }
    return null;
  })();

  const ProductDescription =
    productDescriptionBody || productProperties.length > 0 ? (
      <Stack direction="column" spacing={1}>
        {productDescriptionBody ? (
          <Typography
            variant="body2"
            component="div"
            sx={{ wordBreak: "break-word" }}
          >
            {productDescriptionBody}
          </Typography>
        ) : null}
        {productProperties.length > 0 ? (
          <PropertiesGridLabeledValue
            properties={productProperties}
            columns={columns}
          />
        ) : null}
      </Stack>
    ) : null;

  return ItemDescription || ProductTitle || ProductDescription ? (
    <Stack direction="column" spacing={1}>
      {ItemDescription}
      {ProductTitle}
      {ProductDescription}
    </Stack>
  ) : null;
};

export const quoteItemHasDescription = (
  item: QuotePreviewItemDescription_ItemFragment,
  productsVisibility: QuoteProductsVisibilityFragment
): boolean => {
  return Boolean(
    itemHasProperties(item) ||
    item.description ||
    item.pendingChangeAttributes.description ||
    (item.product &&
      item.product.title &&
      item.product.titleClientVisibility !== "hide" &&
      productsVisibility.showTitle) ||
    (item.pendingChangeAttributes.productTitle &&
      item.pendingChangeAttributes.productTitleClientVisibility !== "hide" &&
      productsVisibility.showTitle) ||
    (item.product &&
      item.product.description &&
      item.product.descriptionClientVisibility !== "hide" &&
      productsVisibility.showDescription) ||
    (item.pendingChangeAttributes.productDescription &&
      item.pendingChangeAttributes.productDescriptionClientVisibility !==
        "hide" &&
      productsVisibility.showDescription)
  );
};
